/**
 * StaffChatWidget — staff_chat_v1 M4
 * popup floating widget (ปุ่มกลมมุมขวาล่าง -> กล่องแชท)
 * - self-contained: decode token เอง (id/name), เปิด SSE เอง (ทาง A), ใช้ API axios เดิม
 * - ฝังครั้งเดียวใน Layout.js -> โผล่ทุกหน้าฝั่งแชท
 * ES5-ish style ตาม convention ของ repo
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../api';
import { connectSSE } from '../lib/sse'; /* sse_pool_v1: แชร์ connection เดียวกับ Layout/Inbox */
import { useAdaptivePoll } from '../lib/useAdaptivePoll'; /* poll_adaptive_v1 */

// decode JWT payload (atob — มี precedent ใน Shifts.js, ไม่มี jwt-decode package)
function getMe() {
  try {
    var tk = localStorage.getItem('token');
    if (!tk) return null;
    var p = JSON.parse(atob(tk.split('.')[1]));
    return { id: p.id, name: p.name, role: p.role };
  } catch (e) { return null; }
}

var C = {
  green: '#10b981', slate: '#64748b', dark: '#0f172a',
  red: '#ef4444', lightBg: '#f1f5f9', border: '#e2e8f0'
};

function fmtTime(iso) {
  try {
    var d = new Date(iso);
    return ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2);
  } catch (e) { return ''; }
}

export default function StaffChatWidget() {
  var me = getMe();
  var [open, setOpen] = useState(false);
  var [view, setView] = useState('list');     // 'list' | 'chat' | 'new'
  var [convs, setConvs] = useState([]);
  var [activeConv, setActiveConv] = useState(null);
  var [messages, setMessages] = useState([]);
  var [convMembers, setConvMembers] = useState([]); // staff_chat_read_v3: members + last_read_at
  var [draft, setDraft] = useState('');
  var [agents, setAgents] = useState([]);
  var [groupMode, setGroupMode] = useState(false);
  var [groupTitle, setGroupTitle] = useState('');
  var [groupSel, setGroupSel] = useState({});  // {agentId: true}
  var [loading, setLoading] = useState(false);
  var msgEndRef = useRef(null);
  var activeConvRef = useRef(null);
  var openRef = useRef(false);
  // staff_chat_notify_v4: เสียง beep (oscillator) + browser notification
  var audioCtxRef = useRef(null);

  activeConvRef.current = activeConv;
  openRef.current = open;

  // staff_chat_notify_v4: ปลดล็อก AudioContext หลัง user click (browser block ก่อนหน้านั้น)
  useEffect(function () {
    function unlock() {
      try {
        if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      } catch (e) {}
    }
    document.addEventListener('click', unlock);
    document.addEventListener('touchstart', unlock);
    return function () {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  // staff_chat_notify_v4: beep สั้น 2 ครั้ง (sine 880Hz — ต่างจาก Inbox ที่เป็น sawtooth)
  function playBeep() {
    try {
      var ctx = audioCtxRef.current;
      if (!ctx || ctx.state !== 'running') return;
      [0, 0.18].forEach(function (offset) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + offset);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + offset + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + offset + 0.15);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(ctx.currentTime + offset);
        osc.stop(ctx.currentTime + offset + 0.16);
      });
    } catch (e) {}
  }

  // staff_chat_notify_v4: browser notification (เฉพาะตอนอยู่แท็บอื่น/มินิไมซ์)
  function notifyBrowser(title, body) {
    try {
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      if (!document.hidden) return; // อยู่หน้านี้อยู่แล้ว ไม่ต้องเด้ง
      var n = new Notification(title, { body: body, tag: 'staff-chat', icon: '/logo192.png' });
      n.onclick = function () { window.focus(); try { n.close(); } catch (e) {} };
    } catch (e) {}
  }
  var totalUnread = convs.reduce(function (s, c) { return s + (parseInt(c.unread, 10) || 0); }, 0);

  // ---------- data loaders ----------
  var loadConvs = useCallback(function () {
    API.get('/api/staff-chat/conversations').then(function (r) {
      setConvs(r.data.conversations || []);
    }).catch(function () {});
  }, []);

  var loadAgents = useCallback(function () {
    API.get('/api/staff-chat/agents').then(function (r) {
      setAgents(r.data.agents || []);
    }).catch(function () {});
  }, []);

  var loadMessages = useCallback(function (convId) {
    API.get('/api/staff-chat/conversations/' + convId + '/messages').then(function (r) {
      setMessages(r.data.messages || []);
      setConvMembers(r.data.members || []); // staff_chat_read_v3
      // mark read
      API.post('/api/staff-chat/conversations/' + convId + '/read').then(function () {
        loadConvs();
      }).catch(function () {});
    }).catch(function () {});
  }, [loadConvs]);

  // ---------- open conversation ----------
  function openConv(conv) {
    setActiveConv(conv);
    setView('chat');
    loadMessages(conv.id);
  }

  function convTitle(conv) {
    if (conv.type === 'group') return conv.title || 'กลุ่ม';
    // dm: ชื่อคนอื่นที่ไม่ใช่ฉัน
    var others = (conv.members || []).filter(function (m) { return me && m.id !== me.id; });
    return others.length ? others.map(function (m) { return m.name; }).join(', ') : 'แชท';
  }

  // staff_chat_read_v3: สถานะข้อความฝั่งเรา — เทียบ last_read_at ของอีกฝ่ายกับ created_at
  // คืน { text, read } ; read=true -> น้ำเงิน, false -> เทา
  function readStatus(msg) {
    if (!activeConv) return null;
    var others = convMembers.filter(function (mm) { return mm.id !== msg.sender_id; });
    if (others.length === 0) return null;
    var msgTime = new Date(msg.created_at).getTime();
    var readCount = others.filter(function (mm) {
      return mm.last_read_at && new Date(mm.last_read_at).getTime() >= msgTime;
    }).length;
    if (activeConv.type === 'group') {
      if (readCount === 0) return { text: 'ส่งแล้ว', read: false };
      if (readCount === others.length) return { text: 'อ่านแล้วทั้งหมด', read: true };
      return { text: 'อ่านแล้ว ' + readCount + '/' + others.length, read: true };
    }
    // dm
    return readCount > 0 ? { text: 'อ่านแล้ว', read: true } : { text: 'ส่งแล้ว', read: false };
  }

  // ---------- send ----------
  function send() {
    var body = draft.trim();
    if (!body || !activeConv) return;
    setDraft('');
    API.post('/api/staff-chat/conversations/' + activeConv.id + '/messages', { body: body })
      .then(function (r) {
        setMessages(function (prev) { return prev.concat([r.data.message]); });
        loadConvs();
      }).catch(function () { setDraft(body); });
  }

  // ---------- start dm ----------
  function startDm(agentId) {
    setLoading(true);
    API.post('/api/staff-chat/conversations', { type: 'dm', agent_id: agentId })
      .then(function (r) {
        setLoading(false);
        loadConvs();
        // เปิดห้องเลย
        var convId = r.data.id;
        setActiveConv({ id: convId, type: 'dm', members: agents.filter(function (a) { return a.id === agentId; }).map(function (a) { return { id: a.id, name: a.name }; }).concat(me ? [{ id: me.id, name: me.name }] : []) });
        setView('chat');
        loadMessages(convId);
      }).catch(function () { setLoading(false); });
  }

  // ---------- create group ----------
  function createGroup() {
    var title = groupTitle.trim();
    var ids = Object.keys(groupSel).filter(function (k) { return groupSel[k]; });
    if (!title || ids.length === 0) return;
    setLoading(true);
    API.post('/api/staff-chat/conversations', { type: 'group', title: title, agent_ids: ids })
      .then(function (r) {
        setLoading(false);
        setGroupMode(false); setGroupTitle(''); setGroupSel({});
        loadConvs();
        var convId = r.data.id;
        setActiveConv({ id: convId, type: 'group', title: title });
        setView('chat');
        loadMessages(convId);
      }).catch(function () { setLoading(false); });
  }

  // ---------- SSE ----------
  /* sse_pool_v1: เดิม widget เปิด EventSource + เขียน reconnect/backoff ของตัวเองแยกต่างหาก
     ตอนนี้แชร์ connection เดียวกับ Layout/Inbox ผ่าน connectSSE (parse JSON ให้แล้ว) */
  useEffect(function () {
    if (!me) return undefined;

    var conn = connectSSE({
      /* poll_adaptive_v1: กลับมาจากช่วง SSE หลุด → ดึงห้องแชท/badge ใหม่ทันที */
      onConnect: function () { loadConvs(); },

      onStaffMessage: function (data) {
        // staff_chat_owner_v2: agent กรองด้วย member_ids / เจ้าของร้านเห็นทุกห้อง
        var isOwner = me.role === 'tenant';
        if (!isOwner && (!data.member_ids || data.member_ids.indexOf(me.id) === -1)) return;
        // ข้ามข้อความของตัวเอง (เพราะ append ไปแล้วตอน send)
        if (data.sender_id === me.id) { loadConvs(); return; }
        // ถ้ากำลังเปิดห้องนั้นอยู่ -> append + mark read
        if (openRef.current && activeConvRef.current && activeConvRef.current.id === data.conversation_id) {
          loadMessages(data.conversation_id);
        } else {
          loadConvs();
          // staff_chat_notify_v4: เสียง + browser noti
          playBeep();
          notifyBrowser('💬 ' + (data.sender_name || 'พนักงาน'), data.preview || 'ข้อความใหม่');
        }
      },

      onStaffConversationNew: function (data) {
        var isOwner = me.role === 'tenant';
        if (isOwner || (data.member_ids && data.member_ids.indexOf(me.id) > -1)) loadConvs();
      },

      // staff_chat_read_v3: อีกฝ่ายอ่านแล้ว -> อัปเดต last_read_at ของห้องที่เปิดอยู่ (ติ๊กเป็นน้ำเงินสด)
      onStaffRead: function (data) {
        if (openRef.current && activeConvRef.current && activeConvRef.current.id === data.conversation_id) {
          setConvMembers(function (prev) {
            return prev.map(function (mm) {
              return mm.id === data.reader_id ? Object.assign({}, mm, { last_read_at: data.read_at }) : mm;
            });
          });
        }
      },
    });

    return function () { conn.close(); };
  }, [loadConvs, loadMessages]); // eslint-disable-line

  // initial load เมื่อเปิด popup
  useEffect(function () {
    if (open) {
      loadConvs(); loadAgents();
      // staff_chat_notify_v4: ขอสิทธิ์ browser notification ครั้งแรกที่เปิด
      try {
        if (('Notification' in window) && Notification.permission === 'default') {
          Notification.requestPermission();
        }
      } catch (e) {}
    }
  }, [open, loadConvs, loadAgents]);

  // staff_chat_badge_v5: โหลด convs ตั้งแต่ mount (badge ขึ้นทันทีไม่ต้องเปิด popup)
  useEffect(function () {
    loadConvs();
  }, [loadConvs]);
  /* poll_adaptive_v1: fallback เผื่อ SSE หลุด — SSE ต่ออยู่ก็ยืดเป็น 90 วิ
     (staff_message/staff_conversation_new ส่ง event มาให้อยู่แล้ว) */
  useAdaptivePoll(loadConvs, { fastMs: 30000, slowMs: 90000 });

  // auto scroll ลงล่างเมื่อมีข้อความใหม่
  useEffect(function () {
    if (msgEndRef.current) msgEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // staff_chat_sidebar_v1: open/close from the left-sidebar menu + broadcast unread to it
  useEffect(function () {
    function onToggle() { setOpen(function (o) { return !o; }); }
    window.addEventListener('toggle-staff-chat', onToggle);
    return function () { window.removeEventListener('toggle-staff-chat', onToggle); };
  }, []);
  useEffect(function () {
    try { window.dispatchEvent(new CustomEvent('staff-chat-unread', { detail: totalUnread })); } catch (e) {}
  }, [totalUnread]);

  if (!me || (me.role !== 'agent' && me.role !== 'tenant')) return null; // staff_chat_owner_v2: agent + เจ้าของร้าน

  // ---------- styles ----------
  /* dead_code_v1: เคยมีปุ่มลอยกลมมุมขวาล่าง (FAB) พร้อม badge และ animation pulse
     แต่ถูกใส่ display:'none' ไว้ตั้งแต่ staff_chat_sidebar_v1 ที่ย้ายไปเปิดจากเมนูซ้ายแทน
     ตัวนับที่ยังไม่อ่านย้ายไปโชว์ที่เมนูซ้ายผ่าน event 'staff-chat-unread' เรียบร้อยแล้ว
     ลบทั้งปุ่ม + style (sFab/sBadge) + keyframes ทิ้ง — พฤติกรรมเหมือนเดิมเพราะมันซ่อนอยู่แล้ว */
  var sPopup = {
    position: 'fixed', right: 20, bottom: 88, width: 340, height: 480,
    background: '#fff', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
    zIndex: 9999, display: 'flex', flexDirection: 'column', overflow: 'hidden'
  };
  var sHeader = {
    background: C.green, color: '#fff', padding: '12px 14px',
    display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0
  };
  var sBody = { flex: 1, overflowY: 'auto', background: C.lightBg };
  var sInputBar = {
    display: 'flex', alignItems: 'center', gap: 8, padding: 10,
    borderTop: '1px solid ' + C.border, background: '#fff', flexShrink: 0
  };

  function HeaderBtn(props) {
    return <button onClick={props.onClick} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1 }}>{props.children}</button>;
  }

  return (
    <div>
      {/* POPUP — เปิด/ปิดจากเมนูซ้าย ผ่าน event 'toggle-staff-chat' */}
      {open &&
        <div style={sPopup}>
          {/* header */}
          <div style={sHeader}>
            {view === 'chat' || view === 'new'
              ? <HeaderBtn onClick={function () { setView('list'); setActiveConv(null); setGroupMode(false); }}>←</HeaderBtn>
              : <span style={{ fontSize: 18 }}>💬</span>}
            <div style={{ flex: 1, fontWeight: 'bold', fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {view === 'chat' && activeConv ? convTitle(activeConv)
                : view === 'new' ? 'เลือกคน / สร้างกลุ่ม'
                  : 'แชทพนักงาน'}
            </div>
            {view === 'list' && <HeaderBtn onClick={function () { setView('new'); setGroupMode(false); }}>✏️</HeaderBtn>}
            <HeaderBtn onClick={function () { setOpen(false); }}>✕</HeaderBtn>
          </div>

          {/* ===== LIST VIEW ===== */}
          {view === 'list' &&
            <div style={sBody}>
              {convs.length === 0 &&
                <div style={{ padding: 24, textAlign: 'center', color: C.slate, fontSize: 14 }}>
                  ยังไม่มีห้องแชท<br />กด ✏️ มุมขวาบนเพื่อเริ่มคุย
                </div>}
              {convs.map(function (c) {
                var unread = parseInt(c.unread, 10) || 0;
                return (
                  <div key={c.id} onClick={function () { openConv(c); }}
                    style={{ padding: '12px 14px', borderBottom: '1px solid ' + C.border, cursor: 'pointer', background: '#fff', display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: c.type === 'group' ? '#818cf8' : C.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                      {c.type === 'group' ? '👥' : (convTitle(c)[0] || '?')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 'bold', color: C.dark, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{convTitle(c)}</div>
                      <div style={{ color: C.slate, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.last_body || '—'}</div>
                    </div>
                    {unread > 0 && <span style={{ background: C.red, color: '#fff', borderRadius: 10, fontSize: 12, fontWeight: 'bold', padding: '1px 7px', flexShrink: 0 }}>{unread}</span>}
                  </div>
                );
              })}
            </div>}

          {/* ===== NEW (เลือกคน / สร้างกลุ่ม) ===== */}
          {view === 'new' &&
            <div style={sBody}>
              <div style={{ display: 'flex', gap: 6, padding: 10 }}>
                <button onClick={function () { setGroupMode(false); }} style={{ flex: 1, padding: 8, borderRadius: 8, border: 'none', cursor: 'pointer', background: !groupMode ? C.green : C.lightBg, color: !groupMode ? '#fff' : '#475569', fontWeight: 'bold' }}>คุยส่วนตัว</button>
                <button onClick={function () { setGroupMode(true); }} style={{ flex: 1, padding: 8, borderRadius: 8, border: 'none', cursor: 'pointer', background: groupMode ? C.green : C.lightBg, color: groupMode ? '#fff' : '#475569', fontWeight: 'bold' }}>สร้างกลุ่ม</button>
              </div>

              {groupMode &&
                <div style={{ padding: '0 10px 8px' }}>
                  <input value={groupTitle} onChange={function (e) { setGroupTitle(e.target.value); }} placeholder="ชื่อกลุ่ม..."
                    style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid ' + C.border, boxSizing: 'border-box' }} />
                </div>}

              {agents.map(function (a) {
                return (
                  <div key={a.id}
                    onClick={function () {
                      if (groupMode) { setGroupSel(function (prev) { var n = Object.assign({}, prev); n[a.id] = !n[a.id]; return n; }); }
                      else { startDm(a.id); }
                    }}
                    style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.border, cursor: 'pointer', background: '#fff', display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{a.name ? a.name[0] : '?'}</div>
                    <div style={{ flex: 1, color: C.dark, fontSize: 14 }}>{a.name}<div style={{ color: C.slate, fontSize: 12 }}>{a.role}</div></div>
                    {groupMode && <span style={{ fontSize: 18 }}>{groupSel[a.id] ? '☑️' : '⬜'}</span>}
                  </div>
                );
              })}

              {groupMode &&
                <div style={{ padding: 10 }}>
                  <button onClick={createGroup} disabled={loading || !groupTitle.trim() || Object.keys(groupSel).filter(function (k) { return groupSel[k]; }).length === 0}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: 'none', cursor: 'pointer', background: C.green, color: '#fff', fontWeight: 'bold', opacity: loading ? 0.6 : 1 }}>
                    สร้างกลุ่ม
                  </button>
                </div>}
            </div>}

          {/* ===== CHAT VIEW ===== */}
          {view === 'chat' &&
            <div style={sBody}>
              <div style={{ padding: 12 }}>
                {messages.map(function (m) {
                  var mine = me && m.sender_id === me.id;
                  var st = mine ? readStatus(m) : null;
                  return (
                    <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                      {!mine && activeConv && activeConv.type === 'group' &&
                        <div style={{ fontSize: 11, color: C.slate, marginBottom: 2 }}>{m.sender_name}</div>}
                      <div style={{ maxWidth: '75%', padding: '8px 12px', borderRadius: 14, background: mine ? C.green : '#fff', color: mine ? '#fff' : C.dark, fontSize: 14, wordBreak: 'break-word', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                        {m.body}
                      </div>
                      <div style={{ fontSize: 10, color: C.slate, marginTop: 2, display: 'flex', gap: 5, alignItems: 'center' }}>
                        <span>{fmtTime(m.created_at)}</span>
                        {st && <span style={{ color: st.read ? '#3b82f6' : C.slate, fontWeight: st.read ? 'bold' : 'normal' }}>{st.read ? '✓✓ ' : '✓ '}{st.text}</span>}
                      </div>
                    </div>
                  );
                })}
                <div ref={msgEndRef} />
              </div>
            </div>}

          {/* input bar (เฉพาะ chat) */}
          {view === 'chat' &&
            <div style={sInputBar}>
              <input value={draft} onChange={function (e) { setDraft(e.target.value); }}
                onKeyDown={function (e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="พิมพ์ข้อความ..."
                style={{ flex: 1, padding: '8px 12px', borderRadius: 18, border: '1px solid ' + C.border, outline: 'none', fontSize: 14 }} />
              <button onClick={send} disabled={!draft.trim()}
                style={{ background: C.green, color: '#fff', border: 'none', borderRadius: '50%', width: 38, height: 38, cursor: 'pointer', fontSize: 16, opacity: draft.trim() ? 1 : 0.5, flexShrink: 0 }}>➤</button>
            </div>}
        </div>}
    </div>
  );
}
